import React, { FC, memo } from 'react';
import classNames from 'classnames';
import { get } from 'lodash';
import { useFormikContext } from 'formik';
// Import Types
import { JSONFormBuilderFormikContextType, NestedArrayJsonType } from '../../types';
// Import Components
import { Button, ExtendedInput } from '@geneui/components';
import DynamicNestedFormBuilder from '../DynamicNestedFormBuilder';
// Import Styles
import styles from '../DynamicNestedFormBuilder/dynamicNestedFormBuilder.module.scss';

interface Props {
    isNested?: boolean;
    disabled?: boolean;
    shouldNotBeNested?: boolean;
    index: number;
    formFieldKey: string;
    fieldValueType?: 'text' | 'number';
    fieldValue: NestedArrayJsonType;
    handleRemove: () => void;
    handleClone: () => void;
}

const KeyValueFormFields: FC<Props> = (props) => {
    const {
        isNested,
        disabled,
        shouldNotBeNested,
        index,
        formFieldKey,
        fieldValue,
        fieldValueType = 'text',
        handleRemove,
        handleClone,
    } = props;
    const { errors, touched, handleChange, handleBlur } = useFormikContext<JSONFormBuilderFormikContextType>();

    const keyName = `${formFieldKey}.${index}.0`;
    const valueName = `${formFieldKey}.${index}.1`;

    const keyError = get(errors, keyName);
    const keyTouched = get(touched, keyName);
    const valueError = get(errors, valueName);
    const valueTouched = get(touched, valueName);

    return (
        <>
            {Array.isArray(fieldValue[1]) ? (
                <DynamicNestedFormBuilder
                    isNested={true}
                    formKey={valueName}
                    parentFormKey={keyName}
                    fieldValueType={fieldValueType}
                    disabled={disabled}
                    shouldNotBeNested={shouldNotBeNested}
                    handleRemoveParent={handleRemove}
                    handleCloneParent={handleClone}
                />
            ) : (
                <div className={classNames(styles.keyValueFieldsContainer, { [styles.nested]: isNested })}>
                    <div className={styles.keyValueFields}>
                        <ExtendedInput
                            type="text"
                            name={keyName}
                            label="Key"
                            title="Key"
                            placeholder="Key"
                            labelAppearance="swap"
                            value={fieldValue[0]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            errorText={keyError}
                            isValid={keyTouched ? !keyError : undefined}
                            disabled={disabled}
                        />
                        <ExtendedInput
                            type={fieldValueType}
                            name={valueName}
                            label="Value"
                            title="Value"
                            placeholder="Value"
                            labelAppearance="swap"
                            value={fieldValue[1]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            errorText={valueError}
                            isValid={valueTouched ? !valueError : undefined}
                            disabled={disabled}
                        />
                    </div>

                    {!disabled && (
                        <div className={styles.fieldActions}>
                            <Button
                                flexibility="default"
                                appearance="minimal"
                                icon="bc-icon-clone"
                                onClick={handleClone}
                                children={undefined}
                            />
                            <Button
                                flexibility="default"
                                appearance="minimal"
                                icon="bc-icon-trash"
                                onClick={handleRemove}
                                children={undefined}
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default memo(KeyValueFormFields);
